import { useEffect, useState } from 'react'
import { api, type BoardMember } from '../api/client'
import { isNativeApp } from '../native'
import { openAssistant } from '../components/Assistant'
import type { Screen } from '../components/Shell'

/*
  The Company tab.

  Everything here is a fact about the business rather than something the owner does, which
  is the line that lets five tabs cover nine destinations. Home, The Table, Meetings and
  Actions are the doing. This is the having: who sits on the board, what the board has been
  told, what it has been given to read, what the figures say.

  It is a screen, not a menu. That distinction is the whole reason this works where two
  previous bottom bars did not: they kept eight destinations and hid four behind an
  overflow sheet, so half the product lived in a drawer that opened on top of wherever you
  were. A row here navigates, the Company tab stays lit, and the back path is the tab bar
  itself.

  ── Counts ─────────────────────────────────────────────────────────────────

  A column of five identical chevrons tells an owner nothing about which one is worth
  opening. The counts are what make the rows differ, so they are worth a request. Both
  fetches are the same calls the destination screens already make, and both are tolerant:
  a failure leaves the value null and the row simply renders without it, exactly as Home
  treats its metrics. Nothing here is required for the screen to work.
*/

interface Props {
  company: string | null
  board: BoardMember[]
  onNavigate: (s: Screen) => void
  onSignOut: () => void
}

export function Hub({ company, board, onNavigate, onSignOut }: Props) {
  const [files, setFiles] = useState<number | null>(null)
  const [profile, setProfile] = useState<number | null>(null)

  useEffect(() => {
    void api.get<unknown[]>('/api/cabinet')
      .then(d => setFiles(Array.isArray(d) ? d.length : null))
      .catch(() => setFiles(null))

    void api.get<{ completeness?: number }>('/api/company')
      .then(c => setProfile(typeof c?.completeness === 'number' ? c.completeness : null))
      .catch(() => setProfile(null))
  }, [])

  return (
    <div className="hub">
      <header className="hub__head">
        <span className="hub__disc" aria-hidden>{initialOf(company)}</span>
        <h1 className="hub__name">{company ?? 'Your company'}</h1>
      </header>

      <h2 className="hub__group">Company</h2>
      <div className="hub__rows">
        <Row
          label="Company profile"
          value={profile === null ? null : profile >= 100 ? 'Complete' : `${profile}% complete`}
          onClick={() => onNavigate('company')}
        />
        <Row
          label="The Board"
          value={board.length ? `${board.length} ${board.length === 1 ? 'seat' : 'seats'}` : null}
          onClick={() => onNavigate('board')}
        />
        <Row
          label="File Cabinet"
          value={files === null ? null : `${files} ${files === 1 ? 'file' : 'files'}`}
          onClick={() => onNavigate('cabinet')}
        />
        <Row label="The Numbers" onClick={() => onNavigate('numbers')} />
      </div>

      <h2 className="hub__group">Account</h2>
      <div className="hub__rows">
        {/* The only way into the assistant on a phone: there is no launcher below 900px
            because the bottom-right corner is the tab bar's. */}
        <Row label="Help" value="Ask about Boardium" onClick={openAssistant} />
        <Row label="Settings" onClick={() => onNavigate('settings')} />
        {/*
          App Store guideline 3.1.3(f) is what lets a free companion app to a paid web tool
          skip in-app purchase, on condition that the app names no price and no way to buy.
          Billing lives on Settings on the web; inside the app this row does not exist at
          all, because even the word is a call to action. See src/native.ts.
        */}
        {!isNativeApp() && (
          <Row label="Billing" onClick={() => onNavigate('settings')} />
        )}
        <button className="hub__row hub__row--out" onClick={onSignOut}>
          <span className="hub__label">Sign out</span>
        </button>
      </div>
    </div>
  )
}

/* Label left, value in the quiet grey, chevron right. One row shape for the whole screen. */
function Row({
  label, value, onClick,
}: { label: string; value?: string | null; onClick: () => void }) {
  return (
    <button className="hub__row" onClick={onClick}>
      <span className="hub__label">{label}</span>
      {value && <span className="hub__value">{value}</span>}
      <span className="hub__chev" aria-hidden>
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
             strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="m9 5 7 7-7 7" />
        </svg>
      </span>
    </button>
  )
}

function initialOf(company: string | null): string {
  const first = (company ?? '').trim()[0]
  return first ? first.toUpperCase() : 'B'
}
